import { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Paper from '@material-ui/core/Paper';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';

import api from '~/services/api';
import { IIcd10Code, IIcd10CodeList, IIcd10CodesFilters } from '~/services/api/icd10Codes/types';
import { IIdName } from '~/services/api/types';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error';
import { ISort } from '~/utils/sort/types';
import Button from '~/ui/components/common/Button';
import Loader from '~/ui/components/common/Loader';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import PageHeader from '~/ui/components/reusable/PageHeader';
import Header from './Header/Header';
import Table from './Table/Table';
import DiagnosisModal from './DiagnosisModal/DiagnosisModal';
import { initialSort } from './Table/constants';

import styles from './Diagnoses.module.scss';
import getInitialFilters from './Header/form';

const Diagnoses = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSelectorsLoading, setIsSelectorsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDiagnosisByIdLoading, setIsDiagnosisByIdLoading] = useState(false);
  const [filters, setFilters] = useState<IIcd10CodesFilters>(getInitialFilters());
  const [sort, setSort] = useState<ISort>({ ...initialSort });
  const [createdByUsers, setCreatedByUsers] = useState<IIdName[]>([]);
  const [isCreateDiagnosisModalOpen, setIsCreateDiagnosisModalOpen] = useState(false);
  const [editDiagnosis, setEditDiagnosis] = useState<IIcd10Code | null>(null);
  const [deleteDiagnosis, setDeleteDiagnosis] = useState<IIcd10CodeList>(null);

  const { items: diagnoses, pagination } = useStoreState(state => state.icd10Codes);
  const { onGetIcd10Codes, onGetMoreIcd10Codes, onRemoveIcd10Code } = useStoreActions(
    actions => actions.icd10Codes,
  );
  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);

  const requestParams = useMemo(
    () => ({
      ...filters,
      ...sort,
    }),
    [filters, sort],
  );

  const getData = async () => {
    setIsLoading(true);

    try {
      await onGetIcd10Codes(requestParams);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectors = async () => {
    setIsSelectorsLoading(true);

    try {
      const { data } = await api.icd10Codes.getSelectors();

      setCreatedByUsers(data?.createdByUsers ?? []);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsSelectorsLoading(false);
    }
  };

  const onLoadMore = async () => {
    try {
      await onGetMoreIcd10Codes(requestParams);
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const onDiagnosisSaved = async () => {
    setIsCreateDiagnosisModalOpen(false);
    setEditDiagnosis(null);
    await getData();
    await getSelectors();
  };

  const onEditDiagnosis = async (diagnosisId: number) => {
    try {
      setIsDiagnosisByIdLoading(true);
      const { data } = await api.icd10Codes.getById(diagnosisId);

      setEditDiagnosis(data);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsDiagnosisByIdLoading(false);
    }
  };

  const onDelete = async () => {
    try {
      setIsDeleting(true);
      await onRemoveIcd10Code(deleteDiagnosis.id);
      showNotify('Diagnosis successfully deleted');
      setIsDeleting(false);
      setDeleteDiagnosis(null);
      await getData();
    } catch (e) {
      showError(extractErrorMessage(e));
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    getData();
  }, [requestParams]);

  useEffect(() => {
    getSelectors();
  }, []);

  return (
    <>
      <PageHeader title="Diagnoses">
        <div className={styles.addButton}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            startIcon={<AddCircleOutline />}
            onClick={() => {
              setEditDiagnosis(null);
              setIsCreateDiagnosisModalOpen(true);
            }}
          >
            Add New
          </Button>
        </div>
      </PageHeader>
      <Paper elevation={3} className={styles.container}>
        {!isSelectorsLoading && <Header selectors={createdByUsers} onSubmit={setFilters} />}
        {!isLoading && (
          <InfiniteScroll
            dataLength={diagnoses.length}
            next={onLoadMore}
            hasMore={pagination.hasMore}
            loader={<Loader />}
          >
            <Table
              items={diagnoses}
              sort={sort}
              setSort={setSort}
              onEdit={diagnosis => {
                onEditDiagnosis(diagnosis.id);
              }}
              onDelete={item => {
                setIsDeleting(false);
                setDeleteDiagnosis(item);
              }}
            />
          </InfiniteScroll>
        )}
        {isLoading && <Loader />}
        {isDiagnosisByIdLoading && <Loader />}
      </Paper>

      {isCreateDiagnosisModalOpen && (
        <DiagnosisModal
          diagnosis={null}
          onClose={() => setIsCreateDiagnosisModalOpen(false)}
          onSuccess={onDiagnosisSaved}
        />
      )}

      {editDiagnosis && (
        <DiagnosisModal
          diagnosis={editDiagnosis}
          onClose={() => setEditDiagnosis(null)}
          onSuccess={onDiagnosisSaved}
        />
      )}

      {deleteDiagnosis !== null && (
        <ConfirmModal
          title="Delete Diagnosis"
          description={
            <span>
              Are you sure you want to delete{' '}
              <strong>
                {deleteDiagnosis.name} - {deleteDiagnosis.description}
              </strong>
              ?
            </span>
          }
          isLoading={isDeleting}
          onClose={() => {
            setIsDeleting(false);
            setDeleteDiagnosis(null);
          }}
          onConfirm={onDelete}
        />
      )}
    </>
  );
};

export default Diagnoses;
